import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../hooks/UserContext';
import purchasedFilesService from '../../services/purchasedFilesService';
import toast from 'react-hot-toast';
import './Downloads.css';

const Downloads = () => {
  const { user } = useContext(UserContext);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await purchasedFilesService.getMyFiles();
      if (response.success) {
        setFiles(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des fichiers:', error);
      toast.error('Impossible de charger vos fichiers');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    setDownloading(prev => ({ ...prev, [fileId]: true }));
    try {
      await purchasedFilesService.downloadFile(fileId);
      toast.success(`Téléchargement de ${filename} démarré`);
      
      // Recharger la liste pour mettre à jour le compteur de téléchargements
      await fetchFiles();
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      toast.error('Erreur lors du téléchargement du fichier');
    } finally {
      setDownloading(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['octets', 'Ko', 'Mo', 'Go'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="downloads-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Chargement de vos fichiers...</p>
        </div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="downloads-container">
        <div className="empty-state">
          <i className="fas fa-file-download"></i>
          <h2>Aucun fichier acheté</h2>
          <p>Vous n'avez pas encore acheté de fichiers.</p>
          <a href="/shop" className="btn-primary">Découvrir la boutique</a>
        </div>
      </div>
    );
  }

  return (
    <div className="downloads-container">
      <div className="downloads-header">
        <h1>Mes Téléchargements</h1>
        <p className="subtitle">Retrouvez tous vos fichiers achetés</p>
      </div>

      <div className="files-grid">
        {files.map((file) => (
          <div key={file.id} className="file-card">
            <div className="file-icon">
              {file.mime_type?.startsWith('image/') ? (
                <i className="fas fa-image"></i>
              ) : file.mime_type?.startsWith('video/') ? (
                <i className="fas fa-video"></i>
              ) : (
                <i className="fas fa-file"></i>
              )}
            </div>

            <div className="file-info">
              <h3 className="file-name">{file.original_filename}</h3>
              
              <div className="file-details">
                <span className="file-size">
                  <i className="fas fa-hdd"></i> {formatFileSize(file.file_size)}
                </span>
                <span className="file-downloads">
                  <i className="fas fa-download"></i> {file.download_count} téléchargement(s)
                </span>
              </div>

              {file.product && (
                <div className="product-info">
                  <span className="product-label">Produit:</span>
                  <span className="product-name">{file.product.name}</span>
                </div>
              )}

              {file.order && (
                <div className="order-info">
                  <span className="order-label">Commande:</span>
                  <span className="order-number">#{file.order.order_number}</span>
                  <span className="order-date">{formatDate(file.order.created_at)}</span>
                </div>
              )}

              {file.last_downloaded_at && (
                <div className="last-download">
                  <i className="fas fa-clock"></i>
                  Dernier téléchargement: {formatDate(file.last_downloaded_at)}
                </div>
              )}
            </div>

            <button
              className={`btn-download ${downloading[file.id] ? 'downloading' : ''}`}
              onClick={() => handleDownload(file.id, file.original_filename)}
              disabled={downloading[file.id]}
            >
              {downloading[file.id] ? (
                <>
                  <span className="spinner-small"></span>
                  Téléchargement...
                </>
              ) : (
                <>
                  <i className="fas fa-download"></i>
                  Télécharger
                </>
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Downloads;
